import { useId } from 'react'

import type { Condition } from '../../lib/api'
import { CONDITION_OPTIONS, CONDITION_WORDS } from '../../lib/recordings'

export interface ConditionTilesProps {
  /** The group's accessible name. Rendered visibly unless `hideLabel` — the contract SegmentedControl.tsx:11-22 has. */
  label: string
  /** Both call sites pass it: Record's step title and Baseline's card eyebrow already show the word. */
  hideLabel?: boolean
  /** null = nothing chosen — a real state (§2.2). There is no default, and no prop that adds one. */
  value: Condition | null
  onChange: (value: Condition) => void
  disabled?: boolean
}

/**
 * The two recording conditions, as tiles.
 *
 * ── A RESTYLE, NOT A RE-SEMANTIC (R7) ───────────────────────────────────────
 *
 * The v3 canvas draws this as `role="radiogroup"` with `role="radio"` children
 * and `aria-checked`. It is not built that way, and the reason is the standing
 * decision at `SegmentedControl.tsx:35-38`:
 *
 *   "Deliberately NOT a radio group: a radiogroup's arrow-key roving focus is
 *    the right pattern for a form field with a default, and these controls have
 *    no default."
 *
 * §2.2 requires the recording condition to be EXPLICIT — mixing conditions in
 * one baseline is a correctness bug the UI prevents — so `null` is the starting
 * state and there is nothing for a roving tabindex to land on. `role="group"`
 * plus one tab stop per tile is what the rest of this app does and what its
 * `:focus-visible` ring is built for. `aria-pressed`, not `aria-checked`, for
 * the same reason `ThemeToggle` uses it.
 *
 * ── WHY IT IS NOT `SegmentedControl` WITH A CLASS ───────────────────────────
 *
 * The tiles show the code AND the long form on two lines, which `SegmentedControl`
 * does not render and which its other three call sites (the theme toggle, the
 * specimen, Baseline until S3) must not grow. Same semantics, different box.
 *
 * ── NO WIDTH, NO ARITHMETIC ─────────────────────────────────────────────────
 *
 * It fills its container. Baseline's 560 px cap (R7) is that package's own
 * wrapper, so no `maxWidth` prop, no inline custom property and no `style`
 * attribute ships with no production call site behind it. There is no `<`, `>`
 * or arithmetic anywhere in this file, which is why it needs no `clientScience`
 * row.
 */
export function ConditionTiles({
  label,
  hideLabel = false,
  value,
  onChange,
  disabled = false,
}: ConditionTilesProps) {
  const labelId = useId()

  return (
    <div className="condition-field">
      <span className={hideLabel ? 'visually-hidden' : 'field__label'} id={labelId}>
        {label}
      </span>
      <div className="condition" role="group" aria-labelledby={labelId}>
        {CONDITION_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className="condition__opt"
            aria-pressed={value === option.value}
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            {/* The space is load-bearing: without it the computed accessible
                name is "ECEyes closed — eyes closed" and every `/^EC/` locator
                in the suite still matches, so nothing would report it. */}
            <span className="condition__code">{option.label}</span>{' '}
            <span className="condition__name">{CONDITION_WORDS[option.value]}</span>
            <span className="visually-hidden"> — {option.description}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
